/**
 * Death Sequence — crater, multi-wave particles, shake, flash, eliminated text.
 * Includes both projectile kill and void kill sequences.
 *
 * Returns GameEffect[] instead of calling renderers directly.
 */

import { DEATH, VOID, SKY_DEATH, STRUCTURAL, VOID_PERSONALITY, SIDE_DEATH } from '../constants';
import { getActiveTheme } from '../stages/registry';
import { executeCraterPipeline } from '../terrain/grid';
import { unsettleTanksNearCrater } from '../physics/tanks';
import { spawnSmoothingRegion } from '../terrain/sand-physics';
import { VOID_DEATH_CRATER_SHAPE } from '../terrain/crater-shapes';
import type { GameState } from '../types';
import type { GameEffect } from './effects';
import { transitionPhase } from './effects';
import { msToFrames } from './input-edge';
import { recordTrickKill } from './trick-score';
import { getKillLine, getMoodTier, getMoodFontSize } from './void-personality';
import { lookupTank } from '../tanks';
import { isSoloMode, modeUsesStocks } from './mode';

/** Final kill celebration constants. */
const FINAL_KILL = {
  FREEZE_MS: 1200,
  PARTICLE_SCALE: 2.0,
  ANNOUNCE_FONT_SIZE: 56,
  ANNOUNCE_HOLD: 1.5,
  ANNOUNCE_DELAY: 0.2,
  SHAKE_INTENSITY: 40,
  SHAKE_DECAY: 0.93,
} as const;

/** Check if this death leaves exactly 1 player alive (game-ending kill). */
function isFinalKill(state: GameState, dyingIndex: number): boolean {
  if (isSoloMode(state.modeState.mode)) return false;
  const hasStocks = modeUsesStocks(state.modeState.mode);

  // In stock mode, if the dying player still has stocks after this death,
  // they'll respawn — not a final kill. (Stocks haven't been decremented yet
  // at this point, so >1 means they'll still have stocks remaining.)
  if (hasStocks && state.players[dyingIndex].stocks > 1) return false;

  let remaining = 0;
  for (let i = 0; i < state.players.length; i++) {
    if (i === dyingIndex) continue;
    const p = state.players[i];
    if (p.pendingDeath) continue;
    const alive = hasStocks ? (p.alive || p.stocks > 0) : p.alive;
    if (alive) remaining++;
  }
  return remaining === 1;
}

/** Get the winner's player index for final kill celebration. */
function getWinnerIndex(state: GameState, dyingIndex: number): number {
  const hasStocks = modeUsesStocks(state.modeState.mode);
  for (let i = 0; i < state.players.length; i++) {
    if (i === dyingIndex) continue;
    const p = state.players[i];
    if (p.pendingDeath) continue;
    const alive = hasStocks ? (p.alive || p.stocks > 0) : p.alive;
    if (alive) return i;
  }
  return 0;
}

/** Apply final kill celebration effects — extended freeze, big particles, "GAME!" announcement. */
function applyFinalKillEffects(state: GameState, fx: GameEffect[], dyingIndex: number, x: number, y: number): void {
  const winnerIdx = getWinnerIndex(state, dyingIndex);
  const winner = state.players[winnerIdx];

  // Extended freeze
  state.freezeUntil = Math.max(state.freezeUntil, state.frameCount + msToFrames(FINAL_KILL.FREEZE_MS));

  // Big particle burst in winner's color
  fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [x, y, winner.color, 80, FINAL_KILL.PARTICLE_SCALE] });
  fx.push({ type: 'particles', method: 'spawnExplosion', args: [x, y, 0xffffff, 50] });

  // "GAME!" announcement
  fx.push({
    type: 'announce_screen', text: 'GAME!', color: winner.color,
    fontSize: FINAL_KILL.ANNOUNCE_FONT_SIZE, hold: FINAL_KILL.ANNOUNCE_HOLD,
    delay: FINAL_KILL.ANNOUNCE_DELAY,
  });

  // Heavy screen shake
  fx.push({ type: 'screen_shake', intensity: FINAL_KILL.SHAKE_INTENSITY, decay: FINAL_KILL.SHAKE_DECAY });
  fx.push({ type: 'camera_shake', intensity: FINAL_KILL.SHAKE_INTENSITY * 0.8, decay: FINAL_KILL.SHAKE_DECAY });
}

/** Tutorial mode uses very short death timers so opponent respawns near-instantly. */
const TUTORIAL_DEATH_FREEZE_MS = 100;
const TUTORIAL_DEATH_TIMER_S = 0.15;

/** If a burst window is active, tear it down immediately so the overlay disappears on death. */
function clearBurstOnDeath(state: GameState, fx: GameEffect[]): void {
  if (state.burstActivePlayer >= 0) {
    state.burstActivePlayer = -1;
    state.burstWindowTimer = 0;
    fx.push({ type: 'burst_hide' });
  }
}

/** If defense phase is active, tear it down — unfreeze all players. */
function clearDefenseOnDeath(state: GameState): void {
  if (state.defenseActivePlayer >= 0) {
    // Restore frozen players' action states
    for (const [i, savedState] of state.defensePreFreezeStates) {
      const p = state.players[i];
      if (p.alive) p.actionState = savedState;
    }
    state.defensePreFreezeStates = new Map();
    state.defenseActivePlayer = -1;
    state.defenseQueue = [];
  }
}

/** If lottery is active, tear it down immediately so slots disappear on death. */
function clearLotteryOnDeath(state: GameState, fx: GameEffect[]): void {
  if (state.phase === 'lottery_spin' || state.phase === 'lottery_pick') {
    fx.push({ type: 'lottery_hide' });
  }
}

/** If edgeguard beam is active, kill it so it doesn't stick on screen after death. */
function clearEdgeguardBeamOnDeath(state: GameState): void {
  state.edgeguardBeam = null;
}

/** Remove dead player's vortex portals so they don't persist through respawn or game-over. */
function clearVortexPortalsOnDeath(state: GameState, playerIndex: number): void {
  for (let i = state.vortexHoles.length - 1; i >= 0; i--) {
    if (state.vortexHoles[i].ownerIndex === playerIndex) {
      state.vortexHoles.splice(i, 1);
    }
  }
}

/** Void kill death sequence — distinct from projectile death with void-themed effects. */
export function triggerVoidDeathSequence(
  state: GameState,
  playerIndex: number,
): GameEffect[] {
  const player = state.players[playerIndex];
  // Idempotency guard — prevents double-crater/stats if both clients trigger independently
  if (player.pendingDeath || !player.alive) return [];

  const fx: GameEffect[] = [];
  // Don't set alive=false yet — same pattern as projectile death
  player.pendingDeath = true;
  player.landingTimer = 0;
  player.defenseSubPhase = 'none';

  // Clear multiplayer authority-wait flags — death trumps all phase waits
  state.diWaitForAuthority = false;
  state.burstWaitForAuthority = false;
  state.defenseWaitForAuthority = false;

  // Match stat tracking — attribute kill to whoever knocked them into void
  if (state.matchStats) {
    const killer = player.lastHitBy;
    if (killer >= 0 && killer !== playerIndex) {
      state.matchStats.voidKills[killer]++;
      state.matchStats.kills[killer]++;
    }
  }

  // Trick scoring — void kill
  recordTrickKill(state, playerIndex, 'void', fx);

  // Mood gain on void kill
  state.voidMood = Math.min(100, state.voidMood + VOID_PERSONALITY.MOOD_PER_KILL);
  fx.push({ type: 'void_mood_update', mood: state.voidMood });

  const { x, y, color } = player;

  // Smaller void death crater + wall smoothing
  const voidCraterResult = executeCraterPipeline(x, y, VOID.CRATER_RADIUS, VOID_DEATH_CRATER_SHAPE);
  if (voidCraterResult) {
    fx.push({ type: 'terrain_update', bounds: voidCraterResult.finalBounds });
    fx.push({ type: 'terrain_add_crater', x, y, radius: VOID.CRATER_RADIUS });

    fx.push({ type: 'particles', method: 'spawnTerrainDebris', args: [x, y, VOID.CRATER_RADIUS, 1.0] });

    spawnSmoothingRegion(state, voidCraterResult.finalBounds);

    // Unsettle tanks near void death crater
    unsettleTanksNearCrater(state.players, x, VOID.CRATER_RADIUS);

    // Structural collapse — wireframe fragment debris + sand cascade
    if (voidCraterResult.collapse) {
      const c = voidCraterResult.collapse;
      for (const centroid of c.centroids) {
        fx.push({ type: 'particles', method: 'spawnStructuralDebris', args: [centroid.x, centroid.y, c.cells] });
      }
      const shakeIntensity = Math.min(
        STRUCTURAL.SHAKE_BASE + c.cells * STRUCTURAL.SHAKE_PER_CELL,
        STRUCTURAL.SHAKE_MAX,
      );
      fx.push({ type: 'screen_shake', intensity: shakeIntensity, decay: STRUCTURAL.SHAKE_DECAY });
      spawnSmoothingRegion(state, c.bounds);
      for (const centroid of c.centroids) {
        unsettleTanksNearCrater(state.players, centroid.x, 60);
      }
    }
  }

  // Void-themed particle choreography
  const voidColor = getActiveTheme(state.stageId).voidEdgeColor;
  fx.push({ type: 'particles', method: 'spawnVoidDeathExplosion', args: [x, y, color, state.voidY, voidColor] });

  // Camera + screen shake (consume intensity)
  fx.push({ type: 'camera_shake', intensity: VOID.CONSUME_SHAKE, decay: 0.88 });
  fx.push({ type: 'screen_shake', intensity: VOID.CONSUME_SCREEN_SHAKE, decay: 0.88 });

  // Void-themed flash — magenta multi-flash
  fx.push({ type: 'screen_multi_flash', color: VOID.EDGE_COLOR, alpha: VOID.FLASH_ALPHA });

  // Void-themed tank flash — magenta + deep purple
  fx.push({ type: 'tank_flash', playerIndex, bright: 0xff00ff, dark: 0x1a0033, duration: 0.4 });

  // Kill text: calm = standard "CONSUMED" label, agitated/enraged = personality text
  const killTier = getMoodTier(state.voidMood);
  if (killTier === 'calm') {
    fx.push({ type: 'hud_show_void_eliminated', x, y });
  } else {
    // Personality text replaces the standard label — void's character emerges
    const killLine = getKillLine(state.voidMood);
    fx.push({
      type: 'announce_world',
      text: killLine,
      x,
      y: y - 30,
      color: VOID_PERSONALITY.TEXT_COLOR,
      fontSize: getMoodFontSize(state.voidMood),
      hold: 1.0,
    });
  }
  // KO! world text (void purple)
  fx.push({ type: 'announce_world', text: 'KO!', x, y: y - 60, color: 0x8800ff, fontSize: 56 });

  // Kill feed announcement
  const victimTank = lookupTank(player.tankId).name;
  const killer = player.lastHitBy;
  if (killer >= 0 && killer !== playerIndex) {
    const killerTank = lookupTank(state.players[killer].tankId).name;
    fx.push({ type: 'announce_screen', text: `${killerTank} consumed ${victimTank}`, color: state.players[killer].color, fontSize: 22, hold: 1.5, y: 0.15 });
  } else {
    fx.push({ type: 'announce_screen', text: `${victimTank} consumed by void`, color: 0x8800ff, fontSize: 22, hold: 1.5, y: 0.15 });
  }

  // Edge distortion + consume burst at kill location
  fx.push({ type: 'void_edge_distortion', x, y: state.voidY });
  fx.push({ type: 'void_consume_burst', x, y: state.voidY });

  // Freeze frame (longer for consume cross to animate; tutorial uses short freeze)
  const isTutorial = state.modeState.mode === 'tutorial';
  const freezeMs = isTutorial ? TUTORIAL_DEATH_FREEZE_MS : VOID.CONSUME_FREEZE_MS;
  state.freezeUntil = Math.max(state.freezeUntil, state.frameCount + msToFrames(freezeMs));

  // Final kill celebration
  if (isFinalKill(state, playerIndex)) {
    applyFinalKillEffects(state, fx, playerIndex, x, y);
  }

  // Clear burst/lottery overlays if active
  clearBurstOnDeath(state, fx);
  clearLotteryOnDeath(state, fx);
  clearDefenseOnDeath(state);
  clearEdgeguardBeamOnDeath(state);
  clearVortexPortalsOnDeath(state, playerIndex);

  // Set phase
  transitionPhase(state, 'death_sequence', fx);
  state.turnTimer = isTutorial ? TUTORIAL_DEATH_TIMER_S : VOID.ANIMATION_DURATION / 1000;

  // Camera follows death location
  fx.push({ type: 'camera_follow', x, y });

  return fx;
}

/** Sky kill death sequence — player launched above UPPER_BLAST_ZONE. */
export function triggerSkyDeathSequence(
  state: GameState,
  playerIndex: number,
): GameEffect[] {
  const player = state.players[playerIndex];
  // Idempotency guard — prevents double effects if both clients trigger independently
  if (player.pendingDeath || !player.alive) return [];

  const fx: GameEffect[] = [];
  player.pendingDeath = true;
  player.landingTimer = 0;
  player.defenseSubPhase = 'none';

  // Clear multiplayer authority-wait flags — death trumps all phase waits
  state.diWaitForAuthority = false;
  state.burstWaitForAuthority = false;
  state.defenseWaitForAuthority = false;

  // Match stat tracking — attribute kill to whoever launched them
  if (state.matchStats) {
    const killer = player.lastHitBy;
    if (killer >= 0 && killer !== playerIndex) {
      state.matchStats.skyKills[killer]++;
      state.matchStats.kills[killer]++;
    }
  }

  // Trick scoring — sky kill
  recordTrickKill(state, playerIndex, 'sky', fx);

  const { x, y, color } = player;

  // No crater — they're in the sky

  // Particle burst: white + cyan + player color
  fx.push({ type: 'particles', method: 'spawnExplosion', args: [x, y, 0xffffff, 60] });
  fx.push({ type: 'particles', method: 'spawnExplosion', args: [x, y, 0x88ddff, 40] });
  fx.push({ type: 'particles', method: 'spawnExplosion', args: [x, y, color, 30] });

  // Camera + screen shake (moderate)
  fx.push({ type: 'camera_shake', intensity: SKY_DEATH.SHAKE_INTENSITY, decay: 0.88 });
  fx.push({ type: 'screen_shake', intensity: 35, decay: 0.85 });

  // White flash
  fx.push({ type: 'screen_multi_flash', color: SKY_DEATH.FLASH_COLOR, alpha: SKY_DEATH.FLASH_ALPHA });

  // Tank flash — bright white + sky blue
  fx.push({ type: 'tank_flash', playerIndex, bright: 0xffffff, dark: 0x224466, duration: 0.4 });

  // "LAUNCHED!" floating text (drifts UP)
  fx.push({ type: 'hud_show_sky_eliminated', x, y, color });
  // KO! world text (sky blue)
  fx.push({ type: 'announce_world', text: 'KO!', x, y: y - 60, color: 0x88ddff, fontSize: 56 });

  // Kill feed announcement
  const skyVictimTank = lookupTank(player.tankId).name;
  const skyKiller = player.lastHitBy;
  if (skyKiller >= 0 && skyKiller !== playerIndex) {
    const skyKillerTank = lookupTank(state.players[skyKiller].tankId).name;
    fx.push({ type: 'announce_screen', text: `${skyKillerTank} launched ${skyVictimTank}`, color: state.players[skyKiller].color, fontSize: 22, hold: 1.5, y: 0.15 });
  } else {
    fx.push({ type: 'announce_screen', text: `${skyVictimTank} launched into orbit`, color: 0x88ddff, fontSize: 22, hold: 1.5, y: 0.15 });
  }

  // Freeze frame (tutorial uses short freeze)
  const isTutorial = state.modeState.mode === 'tutorial';
  const skyFreezeMs = isTutorial ? TUTORIAL_DEATH_FREEZE_MS : SKY_DEATH.FREEZE_MS;
  state.freezeUntil = Math.max(state.freezeUntil, state.frameCount + msToFrames(skyFreezeMs));

  // Final kill celebration
  if (isFinalKill(state, playerIndex)) {
    applyFinalKillEffects(state, fx, playerIndex, x, y);
  }

  // Clear burst/lottery overlays if active
  clearBurstOnDeath(state, fx);
  clearLotteryOnDeath(state, fx);
  clearDefenseOnDeath(state);
  clearEdgeguardBeamOnDeath(state);
  clearVortexPortalsOnDeath(state, playerIndex);

  // Set phase
  transitionPhase(state, 'death_sequence', fx);
  state.turnTimer = isTutorial ? TUTORIAL_DEATH_TIMER_S : SKY_DEATH.ANIMATION_DURATION / 1000;

  // Camera follows death location
  fx.push({ type: 'camera_follow', x, y });

  return fx;
}

/** Side kill death sequence — player launched past side blast zones. */
export function triggerSideDeathSequence(
  state: GameState,
  playerIndex: number,
  side: 'left' | 'right',
): GameEffect[] {
  const player = state.players[playerIndex];
  // Idempotency guard
  if (player.pendingDeath || !player.alive) return [];

  const fx: GameEffect[] = [];
  player.pendingDeath = true;
  player.landingTimer = 0;
  player.defenseSubPhase = 'none';

  // Clear multiplayer authority-wait flags
  state.diWaitForAuthority = false;
  state.burstWaitForAuthority = false;
  state.defenseWaitForAuthority = false;

  // Match stat tracking
  if (state.matchStats) {
    const killer = player.lastHitBy;
    if (killer >= 0 && killer !== playerIndex) {
      state.matchStats.kills[killer]++;
    }
  }

  // Trick scoring — side kill
  recordTrickKill(state, playerIndex, 'side', fx);

  const { x, y, color } = player;

  // Particle burst: orange + white + player color
  fx.push({ type: 'particles', method: 'spawnExplosion', args: [x, y, SIDE_DEATH.FLASH_COLOR, 60] });
  fx.push({ type: 'particles', method: 'spawnExplosion', args: [x, y, 0xffffff, 40] });
  fx.push({ type: 'particles', method: 'spawnExplosion', args: [x, y, color, 30] });

  // Camera + screen shake
  fx.push({ type: 'camera_shake', intensity: SIDE_DEATH.SHAKE_INTENSITY, decay: 0.88 });
  fx.push({ type: 'screen_shake', intensity: 35, decay: 0.85 });

  // Orange flash
  fx.push({ type: 'screen_multi_flash', color: SIDE_DEATH.FLASH_COLOR, alpha: SIDE_DEATH.FLASH_ALPHA });

  // Tank flash — orange + dark
  fx.push({ type: 'tank_flash', playerIndex, bright: SIDE_DEATH.FLASH_COLOR, dark: 0x331100, duration: 0.4 });

  // "BLASTED!" floating text + KO!
  fx.push({ type: 'hud_show_side_eliminated', x, y, color });
  fx.push({ type: 'announce_world', text: 'KO!', x, y: y - 60, color: SIDE_DEATH.TEXT_COLOR, fontSize: 56 });

  // Kill feed announcement
  const sideVictimTank = lookupTank(player.tankId).name;
  const sideKiller = player.lastHitBy;
  if (sideKiller >= 0 && sideKiller !== playerIndex) {
    const sideKillerTank = lookupTank(state.players[sideKiller].tankId).name;
    fx.push({ type: 'announce_screen', text: `${sideKillerTank} blasted ${sideVictimTank}`, color: state.players[sideKiller].color, fontSize: 22, hold: 1.5, y: 0.15 });
  } else {
    fx.push({ type: 'announce_screen', text: `${sideVictimTank} blasted off the ${side}`, color: SIDE_DEATH.TEXT_COLOR, fontSize: 22, hold: 1.5, y: 0.15 });
  }

  // Freeze frame
  const isSideTutorial = state.modeState.mode === 'tutorial';
  const sideFreezeMs = isSideTutorial ? TUTORIAL_DEATH_FREEZE_MS : SIDE_DEATH.FREEZE_MS;
  state.freezeUntil = Math.max(state.freezeUntil, state.frameCount + msToFrames(sideFreezeMs));

  // Final kill celebration
  if (isFinalKill(state, playerIndex)) {
    applyFinalKillEffects(state, fx, playerIndex, x, y);
  }

  // Clear overlays
  clearBurstOnDeath(state, fx);
  clearLotteryOnDeath(state, fx);
  clearDefenseOnDeath(state);
  clearEdgeguardBeamOnDeath(state);
  clearVortexPortalsOnDeath(state, playerIndex);

  // Set phase
  transitionPhase(state, 'death_sequence', fx);
  state.turnTimer = isSideTutorial ? TUTORIAL_DEATH_TIMER_S : SIDE_DEATH.FREEZE_MS / 1000;

  // Camera follows death location
  fx.push({ type: 'camera_follow', x, y });

  return fx;
}
